// Modules
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import { useDispatch } from "react-redux";
import { useSWRConfig } from "swr";
import get from "lodash.get";
// Hooks
import { useModalEligibility } from "@/hooks/useModalEligibility";
// Components
import AppLayout from "../AppLayout";
// Helpers
import { triggerEnterAppEvent } from "../../helpers/gtm";
import { FALLBACK_ROUTES_BY_ROLE, ROUTES } from "@/helpers/routes";
import { logout } from "./helpers/authentication";
import {
    STATUS_ACTIVE,
    STATUS_DELETED,
    STATUS_DISABLED,
    STATUS_PENDING,
    STATUS_WAITING,
    STEP_LIMITED_DASHBOARD
} from "../../helpers/constants";
import { REGISTRATION_STEP, TEMP } from "../../helpers/constants_db";

interface MoveToRouteProps {
    (newRoute: string): void;
}

function Auth({ authProps = { roles: [] as string[] }, children }) {
    const dispatch = useDispatch();
    const router = useRouter();
    const swrConfig = useSWRConfig();

    const { data: session, status } = useSession();
    const [loginEventTriggered, setLoginEventTriggered] = useState(false);

    const { roles } = authProps;
    const isLoading = status === "loading";

    const user = session?.user;
    const userRole = user?.role ?? "";

    const isAuthPage = [ROUTES.login, ROUTES.signup, ROUTES.resetPassword, ROUTES.newPassword].includes(
        router.pathname
    );

    const moveToRoute: MoveToRouteProps = newRoute => {
        router.pathname !== newRoute && router.push(newRoute);
    };

    const limitedDashboard =
        user && user.status === STATUS_WAITING && user[TEMP][REGISTRATION_STEP] === STEP_LIMITED_DASHBOARD;

    /**
     * Custom hook that handles modal eligibility logic for different context-aware modals
     * (e.g., substitute modal, feedback modal).
     *
     * Example use cases:
     * - Show substitute modal if the user is eligible to be replaced.
     *
     */
    useModalEligibility();

    useEffect(() => {
        const fetchData = async () => {
            if (user) {
                if (user.status === STATUS_ACTIVE && !loginEventTriggered) {
                    triggerEnterAppEvent({
                        user_email: user.email,
                        user_role: user.role,
                        user_name: user.name
                    });
                    setLoginEventTriggered(true);
                } else if (loginEventTriggered) {
                    setLoginEventTriggered(false);
                }
            }
        };

        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        if (isLoading) {
            return; // Do nothing while loading
        }

        const isUser = !!user;

        switch (true) {
            case !isUser && !isAuthPage:
                // If not authenticated, force log in
                moveToRoute(ROUTES.login);
                break;
            case user &&
                (user.status === STATUS_PENDING ||
                    (user.status === STATUS_WAITING && user[TEMP][REGISTRATION_STEP] !== STEP_LIMITED_DASHBOARD)):
                // If authenticated but in onboarding process, force onboarding redirect
                moveToRoute(ROUTES.onboarding);
                break;
            case user && user.status !== STATUS_ACTIVE && user.status !== STATUS_WAITING:
                // If authenticates but status is not active, force logout
                logout(swrConfig);
                break;
            case user && (isAuthPage || router.pathname === ROUTES.onboarding):
                // If authenticated but trying to access auth pages, force dashboard redirect
                moveToRoute(ROUTES.dashboard);
                break;
            default:
                break;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session, status, isAuthPage, dispatch]);

    if (user) {
        // If already logged in and trying to access auth pages
        // OR in onboarding process and trying to access other pages, block it
        const isInvalidAccess =
            isAuthPage ||
            [STATUS_DELETED, STATUS_DISABLED].includes(user.status) ||
            (user.status === STATUS_PENDING && router.pathname !== ROUTES.onboarding) ||
            (user.status === STATUS_WAITING && !limitedDashboard && router.pathname !== ROUTES.onboarding);
        if (isInvalidAccess) {
            return null;
        }

        if (!roles || roles.includes(userRole)) {
            const mainLayout = user.status === STATUS_ACTIVE || limitedDashboard;
            return <AppLayout mainLayout={mainLayout}>{children}</AppLayout>;
        }

        const newRoute = get(FALLBACK_ROUTES_BY_ROLE, userRole, undefined) || ROUTES.dashboard;
        moveToRoute(newRoute);
        return null;
    }

    if (isAuthPage && !isLoading) {
        return <AppLayout>{children}</AppLayout>;
    }

    return null;
}

export default Auth;
